/**
 * @file Infrastructure.tsx
 * @description Displays live availability of external VPS services monitored by the server-side
 * poller (GET /api/vps-status). Renders a card grid with one card per target showing its
 * up/down/unknown status, HTTP code, latency, time since the last status change, and the time of
 * the last check. Data is refreshed by polling every 30 seconds; the page degrades safely when
 * the backend endpoint is unavailable by showing an inline error state.
 * @author Son Nguyen <hoangson091104@gmail.com>
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { Server, RefreshCw, Clock, ExternalLink, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import type { VpsTargetStatus } from "../lib/api";
import { eventBus } from "../lib/eventBus";
import { EmptyState } from "../components/EmptyState";
import { timeAgo } from "../lib/format";

const POLL_INTERVAL_MS = 30_000;

const STATUS_STYLES: Record<
  VpsTargetStatus["status"],
  { dot: string; badge: string; labelKey: string; pulse: boolean }
> = {
  up: {
    dot: "bg-emerald-400",
    badge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    labelKey: "statusUp",
    pulse: true,
  },
  down: {
    dot: "bg-red-400",
    badge: "bg-red-500/10 text-red-400 border-red-500/20",
    labelKey: "statusDown",
    pulse: true,
  },
  unknown: {
    dot: "bg-gray-400",
    badge: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    labelKey: "statusUnknown",
    pulse: false,
  },
};

function TargetCard({ target }: { target: VpsTargetStatus }) {
  const { t } = useTranslation("infrastructure");
  const style = STATUS_STYLES[target.status];

  return (
    <div className="card-hover p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${style.dot} ${
              style.pulse ? "animate-pulse-dot" : ""
            }`}
            aria-hidden="true"
          />
          <h3 className="text-sm font-medium text-gray-100 truncate">{target.name}</h3>
        </div>
        <span className={`badge ${style.badge}`}>{t(style.labelKey)}</span>
      </div>

      <a
        href={target.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs font-mono text-gray-500 hover:text-gray-300 transition-colors truncate"
      >
        <ExternalLink className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{target.url}</span>
      </a>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">{t("httpCode")}</span>
          <span className="font-mono text-gray-300">{target.httpCode ?? "—"}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">{t("latency")}</span>
          <span className="font-mono text-gray-300">
            {target.latencyMs != null ? `${target.latencyMs} ms` : "—"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">{t("since")}</span>
          <span className="text-gray-300">
            {target.lastChange ? timeAgo(target.lastChange) : "—"}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-gray-500">{t("lastChecked")}</span>
          <span className="text-gray-300">
            {target.lastChecked ? timeAgo(target.lastChecked) : "—"}
          </span>
        </div>
      </div>
    </div>
  );
}

export function Infrastructure() {
  const { t } = useTranslation("infrastructure");
  const [targets, setTargets] = useState<VpsTargetStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const wsConnected = useSyncExternalStore(eventBus.onConnection, () => eventBus.connected);

  const load = useCallback(async () => {
    try {
      const result = await api.vpsStatus.get();
      setTargets(result.targets);
      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  const upCount = targets?.filter((x) => x.status === "up").length ?? 0;
  const downCount = targets?.filter((x) => x.status === "down").length ?? 0;

  return (
    <div className="animate-fade-in space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
              <Server className="w-4.5 h-4.5 text-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-100">{t("title")}</h1>
                {wsConnected ? (
                  <span className="flex items-center gap-1.5 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
                    {t("common:live")}
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-[11px] text-gray-400 bg-gray-500/10 border border-gray-500/20 px-2 py-0.5 rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    {t("common:offline")}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 flex items-center gap-2">
                {t("subtitle")}
                <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 bg-surface-2 border border-border px-2 py-0.5 rounded-md font-mono ml-2">
                  <Clock className="w-3 h-3" />
                  {lastUpdate.toLocaleTimeString()}
                </span>
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {targets && targets.length > 0 && (
            <span className="text-xs text-gray-500">
              <span className="text-emerald-400 font-medium">{upCount}</span>
              {" / "}
              <span className={downCount > 0 ? "text-red-400 font-medium" : "text-gray-400"}>
                {downCount}
              </span>{" "}
              {t("upDownSummary")}
            </span>
          )}
          <button onClick={load} className="btn-ghost" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {t("common:refresh")}
          </button>
        </div>
      </div>

      {/* Error state (endpoint unreachable) */}
      {error && !targets && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm text-gray-200">{t("loadError")}</p>
            <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !targets && !error && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-pulse h-32 bg-surface-2 rounded-xl" />
          ))}
        </div>
      )}

      {/* Target grid */}
      {targets && targets.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {targets.map((target) => (
            <TargetCard key={target.id} target={target} />
          ))}
        </div>
      )}

      {/* Empty state */}
      {targets && targets.length === 0 && (
        <EmptyState icon={Server} title={t("emptyTitle")} description={t("emptyDescription")} />
      )}
    </div>
  );
}
